/**
 * Sistema de Auto-Invalidação Inteligente de Cache
 * Monitora mudanças e invalida cache automaticamente
 */
import { cacheService, CachePresets } from './CacheService'

export interface InvalidationRule {
  // Padrão de URL que dispara a invalidação
  urlPattern: string | RegExp
  
  // Métodos HTTP que disparam invalidação
  methods: string[]
  
  // Tags a serem invalidadas
  tags: string[]
  
  // Padrões de chave a serem invalidados
  keyPatterns?: (string | RegExp)[]
  
  // Delay antes da invalidação (para operações batch)
  delay?: number
  
  // Condição customizada para invalidação
  condition?: (url: string, method: string, data?: any) => boolean
}

export class CacheInvalidationService {
  private static instance: CacheInvalidationService
  private rules: InvalidationRule[] = []
  private pendingInvalidations = new Map<string, NodeJS.Timeout>()

  static getInstance(): CacheInvalidationService {
    if (!CacheInvalidationService.instance) {
      CacheInvalidationService.instance = new CacheInvalidationService()
    }
    return CacheInvalidationService.instance
  }

  constructor() {
    this.setupDefaultRules()
  }

  /**
   * Configura regras padrão de invalidação
   */
  private setupDefaultRules(): void {
    // Regras para autenticação
    this.addRule({
      urlPattern: /\/api\/auth\/(login|logout|refresh)/,
      methods: ['POST'],
      tags: [CachePresets.TAGS.AUTH, CachePresets.TAGS.USER]
    })

    // Regras para projetos
    this.addRule({
      urlPattern: /\/api\/projetos/,
      methods: ['POST', 'PUT', 'DELETE'],
      tags: [CachePresets.TAGS.PROJECT, CachePresets.TAGS.FILE_TREE],
      keyPatterns: [/^projects:/, /^files:/]
    })

    // Regras para usuários
    this.addRule({
      urlPattern: /\/api\/(admin\/)?usuarios/,
      methods: ['POST', 'PUT', 'DELETE'],
      tags: [CachePresets.TAGS.USER],
      keyPatterns: [/^user:/]
    })

    // Regras para clientes
    this.addRule({
      urlPattern: /\/api\/(admin\/)?clientes/,
      methods: ['POST', 'PUT', 'DELETE'],
      tags: [CachePresets.TAGS.ADMIN],
      keyPatterns: [/^clients:/]
    })

    // Regras para arquivos
    this.addRule({
      urlPattern: /\/api\/arquivos/,
      methods: ['POST', 'PUT', 'DELETE'],
      tags: [CachePresets.TAGS.FILE_TREE, CachePresets.TAGS.PDF],
      keyPatterns: [/^files:/, /^pdf:/]
    })

    // Regras para convites
    this.addRule({
      urlPattern: /\/api\/convites/,
      methods: ['POST', 'PUT', 'DELETE'],
      tags: [CachePresets.TAGS.ADMIN],
      keyPatterns: [/^invites:/]
    })

    // Invalidação em mudanças de perfil
    this.addRule({
      urlPattern: /\/api\/me/,
      methods: ['PUT', 'PATCH'],
      tags: [CachePresets.TAGS.USER],
      keyPatterns: [/^user:/]
    })
  }

  /**
   * Adiciona uma regra de invalidação
   */
  addRule(rule: InvalidationRule): void {
    this.rules.push(rule)
  }

  /**
   * Remove regras por padrão de URL
   */
  removeRule(urlPattern: string | RegExp): void {
    this.rules = this.rules.filter(rule => {
      if (typeof urlPattern === 'string' && typeof rule.urlPattern === 'string') {
        return rule.urlPattern !== urlPattern
      }
      return rule.urlPattern.toString() !== urlPattern.toString()
    })
  }

  /**
   * Processa uma requisição e invalida cache se necessário
   */
  processRequest(url: string, method: string, data?: any): void {
    const matchingRules = this.findMatchingRules(url, method)
    
    for (const rule of matchingRules) {
      // Verificar condição customizada se existir
      if (rule.condition && !rule.condition(url, method, data)) {
        continue
      }

      // Aplicar invalidação com delay se especificado
      if (rule.delay && rule.delay > 0) {
        this.scheduleInvalidation(rule, rule.delay)
      } else {
        this.applyInvalidation(rule)
      }
    }
  }

  /**
   * Encontra regras que correspondem à requisição
   */
  private findMatchingRules(url: string, method: string): InvalidationRule[] {
    return this.rules.filter(rule => {
      // Verificar método
      if (!rule.methods.includes(method.toUpperCase())) {
        return false
      }

      // Verificar padrão de URL
      if (typeof rule.urlPattern === 'string') {
        return url.includes(rule.urlPattern)
      } else {
        return rule.urlPattern.test(url)
      }
    })
  }

  /**
   * Agenda invalidação com delay
   */
  private scheduleInvalidation(rule: InvalidationRule, delay: number): void {
    const ruleKey = this.getRuleKey(rule)
    
    // Cancelar invalidação pendente se existir
    if (this.pendingInvalidations.has(ruleKey)) {
      clearTimeout(this.pendingInvalidations.get(ruleKey)!)
    }

    // Agendar nova invalidação
    const timeout = setTimeout(() => {
      this.applyInvalidation(rule)
      this.pendingInvalidations.delete(ruleKey)
    }, delay)

    this.pendingInvalidations.set(ruleKey, timeout)
  }

  /**
   * Aplica invalidação imediatamente
   */
  private applyInvalidation(rule: InvalidationRule): void {
    let invalidatedCount = 0

    // Invalidar por tags
    if (rule.tags.length > 0) {
      invalidatedCount += cacheService.invalidateByTags(rule.tags)
    }

    // Invalidar por padrões de chave
    if (rule.keyPatterns) {
      for (const pattern of rule.keyPatterns) {
        invalidatedCount += cacheService.invalidateByPattern(pattern)
      }
    }

    if (import.meta.env.DEV && invalidatedCount > 0) {
      console.log(`[CacheInvalidation] Invalidated ${invalidatedCount} entries`, {
        tags: rule.tags,
        keyPatterns: rule.keyPatterns?.map(p => p.toString())
      })
    }
  }

  /**
   * Gera chave única para uma regra
   */
  private getRuleKey(rule: InvalidationRule): string {
    return `${rule.urlPattern.toString()}-${rule.methods.join(',')}-${rule.tags.join(',')}`
  }

  /**
   * Invalida cache manualmente por contexto
   */
  invalidateContext(context: 'auth' | 'user' | 'project' | 'admin' | 'files'): number {
    const tagMap = {
      auth: [CachePresets.TAGS.AUTH],
      user: [CachePresets.TAGS.USER],
      project: [CachePresets.TAGS.PROJECT, CachePresets.TAGS.FILE_TREE],
      admin: [CachePresets.TAGS.ADMIN],
      files: [CachePresets.TAGS.FILE_TREE, CachePresets.TAGS.PDF]
    }

    const tags = tagMap[context] || []
    return cacheService.invalidateByTags(tags)
  }

  /**
   * Limpa todas as invalidações pendentes
   */
  clearPendingInvalidations(): void {
    for (const timeout of this.pendingInvalidations.values()) {
      clearTimeout(timeout)
    }
    this.pendingInvalidations.clear()
  }

  /**
   * Obtém estatísticas de invalidação
   */
  getStats(): {
    totalRules: number
    pendingInvalidations: number
    rulesByContext: Record<string, number>
  } {
    const rulesByContext: Record<string, number> = {}
    
    for (const rule of this.rules) {
      for (const tag of rule.tags) {
        rulesByContext[tag] = (rulesByContext[tag] || 0) + 1
      }
    }

    return {
      totalRules: this.rules.length,
      pendingInvalidations: this.pendingInvalidations.size,
      rulesByContext
    }
  }
}

// Instância global
export const cacheInvalidation = CacheInvalidationService.getInstance()

/**
 * Interceptor para Axios que aplica invalidação automática
 */
export function createCacheInvalidationInterceptor() {
  return {
    // Interceptor de resposta para invalidar cache após mutações bem-sucedidas
    response: (response: any) => {
      const { config } = response
      
      // Só processar se a resposta foi bem-sucedida (2xx)
      if (response.status >= 200 && response.status < 300) {
        cacheInvalidation.processRequest(
          config.url || '',
          config.method?.toUpperCase() || 'GET',
          config.data
        )
      }
      
      return response
    },

    // Interceptor de erro (opcional, para casos específicos)
    error: (error: any) => {
      // Não invalidar cache em caso de erro, apenas propagar
      return Promise.reject(error)
    }
  }
}

/**
 * Utilitários para invalidação manual
 */
export const InvalidationUtils = {
  /**
   * Invalida cache relacionado ao usuário atual
   */
  invalidateCurrentUser(): number {
    return cacheInvalidation.invalidateContext('user')
  },

  /**
   * Invalida cache de projetos
   */
  invalidateProjects(): number {
    return cacheInvalidation.invalidateContext('project')
  },

  /**
   * Invalida cache de arquivos
   */
  invalidateFiles(): number {
    return cacheInvalidation.invalidateContext('files')
  },

  /**
   * Invalida cache de administração
   */
  invalidateAdmin(): number {
    return cacheInvalidation.invalidateContext('admin')
  },

  /**
   * Invalida tudo relacionado à autenticação
   */
  invalidateAuth(): number {
    return cacheInvalidation.invalidateContext('auth')
  },

  /**
   * Invalida cache de um projeto específico
   */
  invalidateProject(projectId: number): number {
    return cacheService.invalidateByPattern(new RegExp(`(projects|files):.*${projectId}`))
  },

  /**
   * Invalida cache de um usuário específico
   */
  invalidateUser(userId: number): number {
    return cacheService.invalidateByPattern(new RegExp(`user:.*${userId}`))
  }
} as const

